import {
  getModel,
  setParamOverride,
  clearParamOverride,
  clearAllOverrides,
  getParamValue,
  stopMotions,
  restartIdle,
} from './avatar.js';

interface AnimStep {
  target: number;
  duration: number;
}

interface ActiveAnim {
  param: string;
  from: number;
  to: number;
  duration: number;
  startTime: number;
  onComplete?: () => void;
}

const animations: ActiveAnim[] = [];
let animFrameId = 0;

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function animTick(): void {
  const now = performance.now();
  const done: number[] = [];

  for (let i = 0; i < animations.length; i++) {
    const a = animations[i];
    const t = Math.min((now - a.startTime) / a.duration, 1);
    const value = a.from + (a.to - a.from) * easeInOutCubic(t);

    // Write to override map — ticker in avatar.ts applies it after model update
    setParamOverride(a.param, value);

    if (t >= 1) {
      done.push(i);
      a.onComplete?.();
    }
  }

  for (let i = done.length - 1; i >= 0; i--) {
    animations.splice(done[i], 1);
  }

  if (animations.length > 0) {
    animFrameId = requestAnimationFrame(animTick);
  }
}

function animate(param: string, target: number, duration: number, onComplete?: () => void): void {
  const current = getParamValue(param);

  // Remove existing animation for this param
  const idx = animations.findIndex((a) => a.param === param);
  if (idx !== -1) animations.splice(idx, 1);

  animations.push({
    param,
    from: current,
    to: target,
    duration,
    startTime: performance.now(),
    onComplete,
  });

  if (animations.length === 1) {
    animFrameId = requestAnimationFrame(animTick);
  }
}

function animateSequence(param: string, steps: AnimStep[], finalCleanup?: boolean): void {
  if (steps.length === 0) {
    if (finalCleanup) clearParamOverride(param);
    return;
  }
  const [first, ...rest] = steps;
  animate(param, first.target, first.duration, () => animateSequence(param, rest, finalCleanup));
}

// Run a full command: stop motions → animate → restart idle
function runAction(fn: () => void, durationMs: number): void {
  stopMotions();
  fn();
  setTimeout(() => {
    clearAllOverrides();
    restartIdle();
  }, durationMs);
}

type CommandHandler = () => void;

const commands: Record<string, CommandHandler> = {
  wave(): void {
    runAction(() => {
      animateSequence('ParamArmRA', [
        { target: 1, duration: 300 },
        { target: 0.5, duration: 200 },
        { target: 1, duration: 200 },
        { target: 0.5, duration: 200 },
        { target: 1, duration: 200 },
        { target: 0, duration: 400 },
      ]);
    }, 1800);
  },

  raise(): void {
    runAction(() => {
      animateSequence('ParamArmRA', [
        { target: 1, duration: 400 },
        { target: 1, duration: 1500 },
        { target: 0, duration: 500 },
      ]);
    }, 2600);
  },

  nod(): void {
    runAction(() => {
      animateSequence('ParamAngleY', [
        { target: 15, duration: 200 },
        { target: -5, duration: 200 },
        { target: 10, duration: 200 },
        { target: 0, duration: 250 },
      ]);
    }, 1100);
  },

  shake(): void {
    runAction(() => {
      animateSequence('ParamAngleX', [
        { target: 20, duration: 150 },
        { target: -20, duration: 200 },
        { target: 15, duration: 150 },
        { target: -15, duration: 150 },
        { target: 0, duration: 200 },
      ]);
    }, 1100);
  },

  smile(): void {
    runAction(() => {
      animate('ParamEyeLSmile', 1, 300);
      animate('ParamEyeRSmile', 1, 300);
      animate('ParamMouthForm', 1, 300);
      setTimeout(() => {
        animate('ParamEyeLSmile', 0, 500);
        animate('ParamEyeRSmile', 0, 500);
        animate('ParamMouthForm', 0, 500);
      }, 1500);
    }, 2300);
  },

  sad(): void {
    runAction(() => {
      animate('ParamBrowLY', -1, 400);
      animate('ParamBrowRY', -1, 400);
      animate('ParamEyeLOpen', 0.5, 400);
      animate('ParamEyeROpen', 0.5, 400);
      animate('ParamAngleY', -10, 500);
      setTimeout(() => {
        animate('ParamBrowLY', 0, 600);
        animate('ParamBrowRY', 0, 600);
        animate('ParamEyeLOpen', 1, 600);
        animate('ParamEyeROpen', 1, 600);
        animate('ParamAngleY', 0, 600);
      }, 1500);
    }, 2400);
  },

  wink(): void {
    runAction(() => {
      animateSequence('ParamEyeROpen', [
        { target: 0, duration: 100 },
        { target: 0, duration: 400 },
        { target: 1, duration: 150 },
      ]);
      animate('ParamMouthForm', 0.7, 200);
      setTimeout(() => animate('ParamMouthForm', 0, 400), 600);
    }, 1200);
  },

  blush(): void {
    runAction(() => {
      animateSequence('ParamCheek', [
        { target: 1, duration: 300 },
        { target: 1, duration: 1500 },
        { target: 0, duration: 500 },
      ]);
      animate('ParamEyeLSmile', 0.5, 300);
      animate('ParamEyeRSmile', 0.5, 300);
      setTimeout(() => {
        animate('ParamEyeLSmile', 0, 500);
        animate('ParamEyeRSmile', 0, 500);
      }, 1800);
    }, 2500);
  },

  shrug(): void {
    runAction(() => {
      animateSequence('ParamShoulder', [
        { target: 1, duration: 300 },
        { target: 1, duration: 800 },
        { target: 0, duration: 400 },
      ]);
      animate('ParamArmLA', 0.5, 300);
      animate('ParamArmRA', 0.5, 300);
      setTimeout(() => {
        animate('ParamArmLA', 0, 400);
        animate('ParamArmRA', 0, 400);
      }, 1100);
    }, 1700);
  },

  lookleft(): void {
    runAction(() => {
      animateSequence('ParamEyeBallX', [
        { target: -1, duration: 300 },
        { target: -1, duration: 1000 },
        { target: 0, duration: 400 },
      ]);
      animateSequence('ParamAngleX', [
        { target: -10, duration: 400 },
        { target: -10, duration: 800 },
        { target: 0, duration: 400 },
      ]);
    }, 1900);
  },

  lookright(): void {
    runAction(() => {
      animateSequence('ParamEyeBallX', [
        { target: 1, duration: 300 },
        { target: 1, duration: 1000 },
        { target: 0, duration: 400 },
      ]);
      animateSequence('ParamAngleX', [
        { target: 10, duration: 400 },
        { target: 10, duration: 800 },
        { target: 0, duration: 400 },
      ]);
    }, 1900);
  },

  idle(): void {
    clearAllOverrides();
    const model = getModel();
    if (!model) return;
    const index = Math.floor(Math.random() * 9);
    try {
      model.motion('Idle', index);
    } catch {}
  },

  tap(): void {
    clearAllOverrides();
    const model = getModel();
    if (!model) return;
    try {
      model.motion('TapBody', 0);
    } catch {}
  },

  breathe(): void {
    runAction(() => {
      animateSequence('ParamBreath', [
        { target: 1, duration: 800 },
        { target: 0, duration: 800 },
        { target: 1, duration: 800 },
        { target: 0, duration: 800 },
      ]);
    }, 3500);
  },

  dance(): void {
    runAction(() => {
      animateSequence('ParamBodyAngleZ', [
        { target: 8, duration: 250 },
        { target: -8, duration: 250 },
        { target: 8, duration: 250 },
        { target: -8, duration: 250 },
        { target: 0, duration: 300 },
      ]);
      animateSequence('ParamArmLA', [
        { target: 1, duration: 250 },
        { target: 0, duration: 250 },
        { target: 1, duration: 250 },
        { target: 0, duration: 300 },
      ]);
      animateSequence('ParamArmRA', [
        { target: 0, duration: 125 },
        { target: 1, duration: 250 },
        { target: 0, duration: 250 },
        { target: 1, duration: 250 },
        { target: 0, duration: 300 },
      ]);
    }, 1600);
  },
};

const aliases: Record<string, string> = {
  'raise hand': 'raise',
  'raise arm': 'raise',
  'hand up': 'raise',
  hi: 'wave',
  hello: 'wave',
  yes: 'nod',
  no: 'shake',
  happy: 'smile',
  grin: 'smile',
  cry: 'sad',
  frown: 'sad',
  'look left': 'lookleft',
  'look right': 'lookright',
  'deep breath': 'breathe',
};

export function executeCommand(input: string): string {
  const normalized = input.trim().toLowerCase();
  const commandName = aliases[normalized] ?? normalized;

  const handler = commands[commandName];
  if (handler) {
    handler();
    return commandName;
  }

  return '';
}

export function getAvailableCommands(): string[] {
  return [
    'wave - wave hand',
    'raise hand - raise right arm',
    'nod - nod yes',
    'shake - shake head no',
    'smile - happy face',
    'sad - sad expression',
    'wink - wink right eye',
    'blush - blush cheeks',
    'shrug - shrug shoulders',
    'look left / look right',
    'idle - random idle motion',
    'tap - tap body motion',
    'breathe - deep breath',
    'dance - little dance',
  ];
}
